import React, { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "./header";
import { SwitchPopup } from "../common/CommonModals";
import SupportEmailModal from "../../screens/profile/components/SupportEmailModal";
import { STRINGS } from "../../constant";
import ChangeExam from "../popup/ChangeExam";
import ChangeFont from "../popup/ChangeFont";
import { ThemeContext } from "../../context/ThemeProvider";
import Sidebar from "./Sidebar";
import { AuthContext } from "../../context/AuthProvider";
import { TestExitModal } from "../test";
import { IS_WHITE_LABEL } from "../../utils/brandFlags";

const MainLayout = () => {
  const [layoutState, setLayoutState] = useState({
    isReset: false,
    changeFont: false,
  });
  const { theme, currentTheme, setCurrentTheme } = useContext(ThemeContext);
  const { profile } = useContext(AuthContext);

  // NOTE: the post-cancel survey/asks are owned by the Profile screen's return
  // router (#4) — return_url is /profile, the guaranteed post-cancel touchpoint.
  // They intentionally no longer render globally here.

  const location = useLocation();
  const navigate = useNavigate();
  const [isTestPage, setIsTestPage] = useState(false);
  const [isTestExitPopup, setIsTestExitPopup] = useState(false);
  const [navigatePath, setNavigatePath] = useState(null);
  const [active, setActive] = useState(location?.pathname);
  const [switchPopup, setSwitchPopup] = useState(false);
  const [isExamSwitching, setIsExamSwitching] = useState(false);

  // Update active page state
  useEffect(() => {
    setIsTestPage(location.pathname === "/test");
    setActive(location?.pathname);
  }, [location.pathname]);

  // Theme toggle function
  const onToggleTheme = () => {
    const body = document.body;
    const newTheme = currentTheme === "light" ? "dark" : "light";
    localStorage.setItem("Theme", newTheme);
    setCurrentTheme(newTheme);
    body.classList.toggle("dark", newTheme === "dark");
  };

  // Empty effect from original code
  useEffect(() => { }, [location]);

  // Handle browser back button
  useEffect(() => {
    const handleBackButton = () => {
      if (location?.pathname === "/test") {
        setIsTestExitPopup(true);
      }
      window.history.pushState(null, document.title, window.location.href);
    };

    window.onpopstate = handleBackButton;
    return () => {
      window.onpopstate = null;
    };
  }, []);

  // Theme-based styles object similar to exam.js pattern
  

  const [isDialogOpen, setDialogOpen] = useState();
  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  // Handle exam switching - just open the dialog, don't show loader yet
  const handleExamSwitch = () => {
    setLayoutState((prev) => ({ ...prev, isReset: true }));
  };

  // Handle exam switch completion
  const handleExamSwitchComplete = () => {
    setLayoutState((prev) => ({ ...prev, isReset: false }));
  };

  return (
    <main
      className="min-h-screen transition-colors duration-300"
      style={{ background: theme?.neutral?.white }}
    >
      {/* Show exam switch loader when exam switching - removed full screen overlay */}

      {/* Sidebar Component */}
      <Sidebar
        isTestPage={isTestPage}
        setIsTestExitPopup={setIsTestExitPopup}
        setNavigatePath={setNavigatePath}
        setActive={setActive}
        setLayoutState={setLayoutState}
        setSwitchPopup={setSwitchPopup}
        onExamSwitch={handleExamSwitch}
      />

      {/* Main Content Area */}
      <div className="lg:pl-[200px] pl-[0px] lg:pb-[0px] pb-[70px] min-h-screen transition-all duration-300 w-full">
        <Header
          onChangeFont={() => setLayoutState((prev) => ({ ...prev, changeFont: true }))}
          onThemeChange={() => onToggleTheme()}
          onProfile={() => navigate("/profile")}
          onContactSupport={() => handleOpenDialog()}
          onUpgrade={IS_WHITE_LABEL ? undefined : () => navigate("/chooseplan")}
        />
        <div className="main-content mx-auto lg:ml-[65px] lg:mr-[25px] pt-6 pb-20 px-2 lg:px-2 py-4 lg:py-4" 
            style={{ backgroundColor: theme?.neutral?.white }}>
          <Outlet key={location.pathname} />
        </div>
      </div>


      <SupportEmailModal
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        theme={theme}
      />

      {/* Popups/Modals */}
      <AnimatePresence>
        {layoutState?.isReset && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
                         <ChangeExam
               state={layoutState}
               onCancle={() => {
                 setLayoutState((prev) => ({ ...prev, isReset: false }));
               }}
               onPress={(e, index) => { }}
               onComplete={handleExamSwitchComplete}
               options={["change"]}
               title={STRINGS.change_bundle}
               desc={STRINGS.change_bundle_messge}
             />
          </motion.div>
        )}

        {layoutState?.changeFont && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ChangeFont
              onChangeFont={() => {
                setLayoutState((prev) => ({
                  ...prev,
                  changeFont: !layoutState?.changeFont,
                }));
              }}
            />
          </motion.div>
        )}

        {isTestExitPopup && isTestPage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <TestExitModal
              isOpen={isTestExitPopup}
              onClose={() => {
                setIsTestExitPopup(false);
              }}
              onConfirm={() => {
                setIsTestPage(false);
                navigate(navigatePath);
                setActive(navigatePath);
              }}
              theme={theme}
            />
          </motion.div>
        )}



        {switchPopup && (
          <SwitchPopup
            isOpen={switchPopup}
            onClose={() => {
              setSwitchPopup(false);
            }}
            onConfirm={() => {
              handleExamSwitch();
              setSwitchPopup(false);
            }}
            title={STRINGS.test_exit_warning}
            confirmText={STRINGS.yes}
            cancelText={STRINGS.no}
            theme={theme}
          />
        )}
      </AnimatePresence>
    </main>
  );
};

export default MainLayout;